import { AppShell, Content } from '../../components/ui/AppShell';
import { Avatar } from '../../components/ui/bits';
import { Calendar, Check, Clock } from '../../components/ui/icons';
import { useOpenProfile } from '../../hooks/useOpenProfile';
import type { DuelDetail, DuelSide } from '../../lib/api';
import { shortRider } from '../../lib/cyclingRound';
import { countdownTo, initials } from '../../lib/format';
import { BackBar } from './BackBar';
import { TermRow } from './TermRow';
import { teamCode } from './helpers';

/** Screen 3 — an incoming challenge to accept or decline (opponent's view). */
export function IncomingChallenge({
  data,
  rival,
  myWins,
  theirWins,
  onBack,
  onAccept,
  onDecline,
  busy
}: {
  data: DuelDetail;
  rival: DuelSide;
  myWins: number;
  theirWins: number;
  onBack: () => void;
  onAccept: () => void;
  onDecline: () => void;
  busy: boolean;
}) {
  const openProfile = useOpenProfile();
  const round = data.round;
  const isCycling = data.sport === 'CYCLING';
  const fixtures = [...data.fixtures].sort((a, b) => a.kickoffAt - b.kickoffAt);
  const rivalFirst = rival.displayName.split(/\s+/)[0];
  const fixtureLine = fixtures
    .slice(0, 3)
    .map((f) =>
      isCycling
        ? `${shortRider(f.riderA?.riderName ?? f.homeTeam)} v ${shortRider(f.riderB?.riderName ?? f.awayTeam)}`
        : `${teamCode(f.homeTeam, f.homeCode)} v ${teamCode(f.awayTeam, f.awayCode)}`
    )
    .join(' · ');
  const hasSeries = myWins > 0 || theirWins > 0;

  return (
    <AppShell>
      <Content>
        <div className="contents md:mx-auto md:block md:w-full md:max-w-lg">
        <BackBar onBack={onBack} label="Friends" eyebrow="New challenge" />

        {/* hero VS */}
        <div className="relative mt-3 overflow-hidden rounded-[30px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] px-5 pb-[22px] pt-6 shadow-[0_24px_46px_-18px_rgba(0,0,0,0.6)]">
          <span className="pointer-events-none absolute -left-[30px] -top-[50px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.2),transparent_70%)]" />
          <div className="relative text-center">
            <span className="text-[10px] font-bold uppercase tracking-eyebrow text-[#9A9AA4]">
              Head-to-head · {round?.label ?? 'Round'}
            </span>
            <div className="mt-[18px] flex items-start justify-between">
              <button
                type="button"
                onClick={() => openProfile(rival.userId)}
                className="tap flex flex-1 flex-col items-center gap-2"
              >
                <Avatar name={rival.displayName} tone={rival.avatarTone} size={60} className="border-2 border-white/15" />
                <span className="text-[13px] font-extrabold tracking-snug text-white">{rival.displayName}</span>
              </button>
              <div className="flex flex-col items-center pt-[14px]">
                <div className="grid h-[42px] w-[42px] rotate-45 place-items-center rounded-[13px] border border-white/15 bg-white/[0.08]">
                  <span className="-rotate-45 text-[13px] font-extrabold text-white">VS</span>
                </div>
              </div>
              <div className="flex flex-1 flex-col items-center gap-2">
                <div className="grid h-[60px] w-[60px] place-items-center rounded-full border-2 border-white/20 bg-gradient-to-br from-night2 to-night text-[24px] font-extrabold text-white shadow-mark">
                  {initials(data.opponent.displayName)}
                </div>
                <span className="text-[13px] font-extrabold tracking-snug text-white">You</span>
              </div>
            </div>
            <div className="mt-[18px] text-[13px] font-semibold text-[#C4C4CC]">
              <span className="font-extrabold text-white">{rivalFirst} challenged you</span> to a {round?.label ?? 'round'} duel
            </div>
            {hasSeries && (
              <div className="mt-[6px] text-[11px] font-semibold text-[#7E7E88]">
                Series · {myWins > theirWins ? `you lead ${myWins}–${theirWins}` : myWins < theirWins ? `${rivalFirst} leads ${theirWins}–${myWins}` : `level ${myWins}–${theirWins}`}
              </div>
            )}
          </div>
        </div>

        {/* the duel terms */}
        <div className="pb-2 pt-[22px]">
          <span className="text-[13px] font-bold tracking-snug text-ink">The duel</span>
        </div>
        <div className="flex flex-col gap-[10px]">
          <TermRow
            Icon={Calendar}
            title={`Same ${isCycling ? 'duels' : 'fixtures'}, ${round?.label ?? 'this round'}`}
            sub={fixtureLine || (isCycling ? 'Call every duel together' : 'Predict the round together')}
          />
          <TermRow
            Icon={Clock}
            title={round ? `${isCycling ? 'Duels' : 'Cards'} lock in ${countdownTo(round.firstKickoffAt)}` : isCycling ? 'Duels lock at the depart' : 'Cards lock at kickoff'}
            sub="Higher combined score wins the duel"
          />
        </div>

        <button
          type="button"
          onClick={onAccept}
          disabled={busy}
          className="tap mt-6 flex w-full items-center justify-center gap-[8px] rounded-[16px] bg-accent py-[16px] text-[16px] font-extrabold text-white shadow-[0_16px_30px_-14px_rgba(10,150,110,0.7)] disabled:opacity-50"
        >
          <Check width={17} height={17} strokeWidth={2.6} />
          Accept the challenge
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          className="tap mt-1 w-full py-[14px] text-center text-[14px] font-bold text-ink3 disabled:opacity-50"
        >
          Decline
        </button>
        </div>
      </Content>
    </AppShell>
  );
}
